import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import DialogContentWrapper from "../../common/components/functional/dialogs/DialogContentWrapper";
import { SalaryType } from "../orders/types/salaryType";
import { useTranslation } from "react-i18next";
import {
  formatTesSalaryCellValue,
  getSalaryAmountFromCell,
  getTesSalaryTableCell,
  sortTesSalaryTableColumns,
  sortTesSalaryTableRows,
} from "./helpers";
import { TesSalaryTableDocument } from "./tesSalaryTablesApi";

export type TesSalaryTablePickerSelection = {
  tesSalaryTableId: string;
  rowId: string;
  columnId: string;
  salaryAmount: string;
};

type Props = {
  tables: TesSalaryTableDocument[];
  salaryType?: SalaryType;
  initialSelection?: {
    tesSalaryTableId: string;
    rowId: string;
    columnId: string;
  } | null;
  tesLabelsById?: Record<string, string>;
  dialogTitle?: string;
  confirmLabel?: string;
  tableFieldLabel?: string;
  onCancel: () => void;
  onConfirm: (selection: TesSalaryTablePickerSelection) => void;
};

const TesSalaryTablePickerDialog = ({
  tables,
  salaryType = SalaryType.Hourly,
  initialSelection,
  tesLabelsById,
  dialogTitle,
  confirmLabel,
  tableFieldLabel,
  onCancel,
  onConfirm,
}: Props) => {
  const { t } = useTranslation("settings");
  const initialTableId =
    initialSelection?.tesSalaryTableId ||
    (tables.length === 1 ? tables[0]?.tesSalaryTableId || "" : "");
  const [tableId, setTableId] = useState(initialTableId);
  const [selectedPosition, setSelectedPosition] = useState<{
    rowId: string;
    columnId: string;
  } | null>(
    initialSelection
      ? {
          rowId: initialSelection.rowId,
          columnId: initialSelection.columnId,
        }
      : null,
  );

  useEffect(() => {
    setTableId(initialTableId);
  }, [initialTableId]);

  const selectedTable = useMemo(
    () => tables.find((table) => table.tesSalaryTableId === tableId) || null,
    [tableId, tables],
  );
  const rows = useMemo(
    () => sortTesSalaryTableRows(selectedTable?.rows || []),
    [selectedTable],
  );
  const columns = useMemo(
    () => sortTesSalaryTableColumns(selectedTable?.columns || []),
    [selectedTable],
  );
  const selectedCell = selectedPosition
    ? getTesSalaryTableCell(
        selectedTable,
        selectedPosition.rowId,
        selectedPosition.columnId,
      )
    : null;
  const selectedSalaryAmount = getSalaryAmountFromCell(
    selectedCell,
    salaryType,
  );

  useEffect(() => {
    if (!selectedTable || !selectedPosition) return;

    const cell = getTesSalaryTableCell(
      selectedTable,
      selectedPosition.rowId,
      selectedPosition.columnId,
    );
    if (!getSalaryAmountFromCell(cell, salaryType)) {
      setSelectedPosition(null);
    }
  }, [salaryType, selectedPosition, selectedTable]);

  return (
    <DialogContentWrapper
      title={dialogTitle || t("tesSalaryTablesTitle")}
      onCancel={onCancel}
      onSave={() => {
        if (!selectedTable || !selectedPosition || !selectedSalaryAmount) {
          return;
        }
        onConfirm({
          tesSalaryTableId: selectedTable.tesSalaryTableId,
          rowId: selectedPosition.rowId,
          columnId: selectedPosition.columnId,
          salaryAmount: selectedSalaryAmount,
        });
      }}
      saveDisabled={
        !selectedTable || !selectedPosition || !selectedSalaryAmount
      }
      customSaveButtonLabel={confirmLabel || t("tesSalaryTablesUseSelectedCell")}
      GridContainerProps={{ rowSpacing: 2 }}
      containerSx={{ minWidth: 900, maxWidth: 1100 }}
    >
      {tables.length > 1 && (
        <>
          <Grid item xs={4} textAlign="end" alignSelf="center">
            <Typography variant="secondary">
              {tableFieldLabel || t("tesSalaryTablesTable")}
            </Typography>
          </Grid>
          <Grid item xs={8}>
            <FormControl fullWidth size="small">
              <InputLabel id="tes-salary-table-picker-select-label" shrink>
                {tableFieldLabel || t("tesSalaryTablesTable")}
              </InputLabel>
              <Select
                labelId="tes-salary-table-picker-select-label"
                label={tableFieldLabel || t("tesSalaryTablesTable")}
                value={tableId}
                displayEmpty
                renderValue={(selected) => {
                  const selectedTableId = selected as string;
                  if (!selectedTableId) {
                    return (
                      <Typography color="text.secondary">
                        {t("common:select...")}
                      </Typography>
                    );
                  }

                  const table = tables.find(
                    (item) => item.tesSalaryTableId === selectedTableId,
                  );
                  return table
                    ? `${table.name} (${table.effectiveDate})`
                    : selectedTableId;
                }}
                onChange={(event) => {
                  setTableId(event.target.value);
                  setSelectedPosition(null);
                }}
              >
                <MenuItem value="" disabled>
                  {t("common:select...")}
                </MenuItem>
                {tables.map((table) => (
                  <MenuItem
                    key={table.tesSalaryTableId}
                    value={table.tesSalaryTableId}
                  >
                    {`${table.name} (${table.effectiveDate})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </>
      )}

      {selectedTable && (
        <Grid item xs={12}>
          <Box display="flex" gap={3} marginBottom={2}>
            <Typography fontWeight={700}>{selectedTable.name}</Typography>
            <Typography color="text.secondary">
              {t("tesSalaryTablesSelectedTes")}:{" "}
              {tesLabelsById?.[selectedTable.tesId] || selectedTable.tesId}
            </Typography>
            <Typography color="text.secondary">
              {t("tesSalaryTablesEffectiveDate")}: {selectedTable.effectiveDate}
            </Typography>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("tesSalaryTablesRow")}</TableCell>
                  {columns.map((column) => (
                    <TableCell
                      key={column.columnId}
                      align="center"
                      sx={{ minWidth: 140 }}
                    >
                      <Box>
                        <Typography fontWeight={700}>{column.label}</Typography>
                        {column.secondaryLabel && (
                          <Typography variant="caption" color="text.secondary">
                            {column.secondaryLabel}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.rowId}>
                    <TableCell>
                      <Box>
                        <Typography fontWeight={700}>{row.label}</Typography>
                        {row.secondaryLabel && (
                          <Typography variant="caption" color="text.secondary">
                            {row.secondaryLabel}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    {columns.map((column) => {
                      const cell = getTesSalaryTableCell(
                        selectedTable,
                        row.rowId,
                        column.columnId,
                      );
                      const isSelected =
                        selectedPosition?.rowId === row.rowId &&
                        selectedPosition?.columnId === column.columnId;

                      return (
                        <TableCell
                          key={`${row.rowId}_${column.columnId}`}
                          align="center"
                          onClick={() => {
                            if (!getSalaryAmountFromCell(cell, salaryType)) {
                              return;
                            }
                            setSelectedPosition({
                              rowId: row.rowId,
                              columnId: column.columnId,
                            });
                          }}
                          sx={{
                            cursor: getSalaryAmountFromCell(cell, salaryType)
                              ? "pointer"
                              : "default",
                            backgroundColor: isSelected
                              ? "rgba(83, 68, 130, 0.12)"
                              : "inherit",
                            outline: isSelected
                              ? "2px solid rgba(83, 68, 130, 0.4)"
                              : "none",
                          }}
                        >
                          {formatTesSalaryCellValue(cell, salaryType)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      )}

    </DialogContentWrapper>
  );
};

export default TesSalaryTablePickerDialog;
